import { evaluateCondition } from '@dynamic/ui';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Condition, MenuItem } from '#/client';
import { getNavigationTarget } from '#/components/Header/utils/menu';
import { MoreItem } from '#/service/config';
import { NavigationType } from '#/service/constants';
import { destinationRoutes, Path } from '#/service/routes';
import { useAuthentication } from '#/store/auth';

import { useConfig, useDataContext } from './';
import { useNavigation } from './useNavigation';

/**
 * A guard that validates if user can access current page by validating
 * conditional routes against location path he wish to visit. It does take into
 * consideration both menu and settings conditions.
 * If condition evaluation returns false then user is redirected to the home page.
 * Registration route is excluded from the guard.
 */
export function usePageGuard() {
  const { pathname } = useLocation();
  const { isLoggedIn, isInitialised } = useAuthentication();

  const { getGeneralContext } = useDataContext();
  const context = getGeneralContext();
  const conditionalRoutes = useConditionalRoutes();
  const { goHome } = useNavigation(`usePageGuard on ${pathname}`);

  useEffect(() => {
    if (!isInitialised) {
      return;
    }

    // registration handles its own redirections
    if (pathname === Path.Registration || !conditionalRoutes) {
      return;
    }

    const condition = conditionalRoutes[pathname];

    // the page we are on is no longer available due to its condition
    const isPageGone = condition && !evaluateCondition(condition, context);

    if (isPageGone) {
      goHome();
    }
  }, [conditionalRoutes, isLoggedIn, context, pathname, goHome, isInitialised]);
}

export function useConditionalRoutes() {
  const {
    more: { settings },
    menu: { mainMenuItems },
  } = useConfig();

  return {
    ...getRoutesFromSettings(settings),
    ...getRoutesFromMenu(mainMenuItems),
  };
}

function getRoutesFromSettings(settings: MoreItem[]): RoutesConditions {
  return settings?.reduce((acc, item) => {
    const pathname = getSettingItemTargetUrl(item);

    return { ...acc, [pathname]: item.condition };
  }, {});
}

function getRoutesFromMenu(menu?: MenuItem[]): RoutesConditions | undefined {
  return menu?.reduce((acc, item) => {
    const pathname = getNavigationTarget(item).internalUrl;

    if (!pathname) {
      return acc;
    }

    return {
      ...acc,
      [pathname]: item.visibility,
    };
  }, {});
}

type RoutesConditions = { [pathname: string]: Condition };

/**
 * It's simplified version of the menu navigation target.
 * We don't care about external urls on purpose
 */
function getSettingItemTargetUrl(item: MoreItem): string {
  const { id: destinationId, type: destinationType } =
    item.action?.params?.destination || {};
  const settingUrl = '/settings/' + item.key;

  switch (destinationType as NavigationType) {
    case NavigationType.Page:
      return `/page/${destinationId}`;
    case NavigationType.Internal:
      return (
        destinationRoutes[destinationId] || `/${destinationId.toLowerCase()}`
      );

    default:
      return settingUrl;
  }
}
